import { useState, useEffect } from "react";
import { useStudioErstellen } from "../../store/studioErstellenStore";
import { useLocation } from "react-router-dom";

import classes from "./FotosInput.module.css";
import ErrorMessage from "./ErrorMessage";

const ALLOWED_IMAGE_TYPES = new Set([
    "image/jpeg",
    "image/jpg",
    "image/pjpeg",
    "image/png",
    "image/x-png",
]);

const ALLOWED_IMAGE_EXT = /\.(jpe?g|png)$/i;

const isAllowedImageFile = (file) => {
    if (!file) return false;
    if (file.type && ALLOWED_IMAGE_TYPES.has(file.type)) return true;
    if (file.name && ALLOWED_IMAGE_EXT.test(file.name)) return true;
    return false;
};

const evaluateDragAllowDrop = (dataTransfer) => {
    const items = Array.from(dataTransfer?.items || []).filter((i) => i.kind === "file");
    if (items.length === 0) {
        if (dataTransfer?.types?.includes("Files")) {
            return true;
        }
        return false;
    }
    const files = items.map((i) => i.getAsFile()).filter(Boolean);
    if (files.length === 0) return true;
    return files.every(isAllowedImageFile);
};

export const SixthStep = ({ validate }) => {
    const { studioData, updateStudioData } = useStudioErstellen();
    const location = useLocation();
    
    const [selectedFiles, setSelectedFiles] = useState(() => {
        if (studioData.images && studioData.images.length > 0) {
            return studioData.images;
        }
        return [];
    });

    const [errors, setErrors] = useState({
        title: "",
    });

    const [isDragOver, setIsDragOver] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [touchStartIndex, setTouchStartIndex] = useState(null);
    const [touchStartY, setTouchStartY] = useState(null);
    const [touchCurrentY, setTouchCurrentY] = useState(null);
      
    const isUsableSelectedPhoto = (photo) => photo?.file instanceof File && photo.file.size > 0;

    // Safety: after reload, JSON-restored photo metadata has no real File object and cannot be published.
    useEffect(() => {
        if (!selectedFiles || selectedFiles.length === 0) return;

        const usableFiles = selectedFiles.filter(isUsableSelectedPhoto);

        if (usableFiles.length !== selectedFiles.length) {
            setSelectedFiles(usableFiles);
            updateStudioData("images", usableFiles);
        }
    }, [selectedFiles, updateStudioData]);

    const handleValidation = () => {
        if (selectedFiles.length < 5 || !selectedFiles.every(isUsableSelectedPhoto)) {
          setErrors({ title: "Mindestens 5 Fotos hinzufügen. Je mehr desto besser." });
          return false;
        }
        return true;
    };

    // Generate UUID with fallback for older browsers
    const generateUUID = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback for older browsers (especially mobile Safari)
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const processFiles = async (files) => {
        try {
            if (!files || files.length === 0) {
                return;
            }

            const list = Array.from(files);
            const allowed = list.filter(isAllowedImageFile);
            const rejectedCount = list.length - allowed.length;

            if (allowed.length === 0) {
                setErrors({ title: "Nur JPG- und PNG-Dateien sind erlaubt." });
                return;
            }

            const newPhotos = await Promise.all(allowed.map(async (file) => {
                const uniqueId = generateUUID();
                const base64Data = await convertFileToBase64(file);
                
                return {
                    id: uniqueId,
                    previewUrl: base64Data, // For preview in StudioProfile
                    file: file,             // For backend upload
                    fileName: `${uniqueId}-${file.name}`,
                    type: file.type
                };
            }));
                        
            const updatedPhotos = [...selectedFiles, ...newPhotos];
            setSelectedFiles(updatedPhotos);
            updateStudioData('images', updatedPhotos);

            if (rejectedCount > 0 && updatedPhotos.length < 5) {
                setErrors({
                    title: "Einige Dateien wurden ignoriert. Mindestens 5 Fotos hinzufügen (nur JPG- und PNG-Dateien).",
                });
            } else if (rejectedCount > 0) {
                setErrors({
                    title: "Einige Dateien wurden ignoriert. Nur JPG- und PNG-Dateien sind erlaubt.",
                });
            } else if (updatedPhotos.length >= 5) {
                setErrors({ title: "" });
            } else {
                setErrors({ title: "Mindestens 5 Fotos hinzufügen. Je mehr desto besser." });
            }
        } catch (error) {
            setErrors({ title: "Fehler beim Hochladen der Bilder. Bitte versuche es erneut." });
        }
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        await processFiles(files);
    };

    const handleRemovePhoto = (photoToRemove) => {
        const updatedFiles = selectedFiles.filter(photo => photo.id !== photoToRemove.id);
        setSelectedFiles(updatedFiles);
        updateStudioData('images', updatedFiles);
        
        // Show error again if we're below 5 photos
        if (updatedFiles.length < 5) {
            setErrors({ title: "Mindestens 5 Fotos hinzufügen. Je mehr desto besser." });
        }
    };

    // Drag and drop handlers for reordering images
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const updatedFiles = [...selectedFiles];
        const draggedItem = updatedFiles[draggedIndex];
        
        // Remove dragged item
        updatedFiles.splice(draggedIndex, 1);
        
        // Insert at new position
        updatedFiles.splice(dropIndex, 0, draggedItem);
        
        setSelectedFiles(updatedFiles);
        updateStudioData('images', updatedFiles);

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Touch handlers for mobile reordering
    const handleTouchStart = (e, index) => {
        const touch = e.touches[0];
        setTouchStartIndex(index);
        setTouchStartY(touch.clientY);
        setTouchCurrentY(touch.clientY);
        setDraggedIndex(index);
    };

    const handleTouchMove = (e) => {
        if (touchStartIndex === null) return;
        
        e.preventDefault(); // Prevent scrolling while dragging
        const touch = e.touches[0];
        setTouchCurrentY(touch.clientY);

        // Find which element we're over using data attribute
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
        const imageWrapper = elements.find(el => 
            el.hasAttribute('data-image-index')
        );
        
        if (imageWrapper) {
            const newIndex = parseInt(imageWrapper.getAttribute('data-image-index'));
            if (!isNaN(newIndex) && newIndex !== touchStartIndex) {
                setDragOverIndex(newIndex);
            } else if (newIndex === touchStartIndex) {
                setDragOverIndex(null);
            }
        } else {
            setDragOverIndex(null);
        }
    };

    const handleTouchEnd = (e) => {
        if (touchStartIndex === null) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const touch = e.changedTouches[0];
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
        const imageWrapper = elements.find(el => 
            el.hasAttribute('data-image-index')
        );

        if (imageWrapper) {
            const dropIndex = parseInt(imageWrapper.getAttribute('data-image-index'));

            if (!isNaN(dropIndex) && dropIndex !== touchStartIndex) {
                const updatedFiles = [...selectedFiles];
                const draggedItem = updatedFiles[touchStartIndex];
                
                updatedFiles.splice(touchStartIndex, 1);
                updatedFiles.splice(dropIndex, 0, draggedItem);
                
                setSelectedFiles(updatedFiles);
                updateStudioData('images', updatedFiles);
            }
        }

        setTouchStartIndex(null);
        setTouchStartY(null);
        setTouchCurrentY(null);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Global touch handlers for better mobile support
    useEffect(() => {
        if (touchStartIndex === null) return;

        const handleGlobalTouchMove = (e) => {
            if (touchStartIndex === null) return;
            e.preventDefault();
            const touch = e.touches[0];
            setTouchCurrentY(touch.clientY);

            const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
            const imageWrapper = elements.find(el => 
                el.hasAttribute('data-image-index')
            );
            
            if (imageWrapper) {
                const newIndex = parseInt(imageWrapper.getAttribute('data-image-index'));
                if (!isNaN(newIndex) && newIndex !== touchStartIndex) {
                    setDragOverIndex(newIndex);
                } else if (newIndex === touchStartIndex) {
                    setDragOverIndex(null);
                }
            } else {
                setDragOverIndex(null);
            }
        };

        const handleGlobalTouchEnd = (e) => {
            if (touchStartIndex === null) return;

            const touch = e.changedTouches[0];
            const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
            const imageWrapper = elements.find(el => 
                el.hasAttribute('data-image-index')
            );

            if (imageWrapper) {
                const dropIndex = parseInt(imageWrapper.getAttribute('data-image-index'));

                if (!isNaN(dropIndex) && dropIndex !== touchStartIndex) {
                    const updatedFiles = [...selectedFiles];
                    const draggedItem = updatedFiles[touchStartIndex];
                    
                    updatedFiles.splice(touchStartIndex, 1);
                    updatedFiles.splice(dropIndex, 0, draggedItem);
                    
                    setSelectedFiles(updatedFiles);
                    updateStudioData('images', updatedFiles);
                }
            }

            setTouchStartIndex(null);
            setTouchStartY(null);
            setTouchCurrentY(null);
            setDraggedIndex(null);
            setDragOverIndex(null);
        };

        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleGlobalTouchEnd);

        return () => {
            document.removeEventListener('touchmove', handleGlobalTouchMove);
            document.removeEventListener('touchend', handleGlobalTouchEnd);
        };
    }, [touchStartIndex, selectedFiles, updateStudioData]);

    // Drag and drop handlers for file upload
    const handleContainerDragOver = (event) => {
        // Don't handle container drag if we're reordering items
        if (draggedIndex !== null) {
            return;
        }
        
        event.preventDefault();
        event.stopPropagation();
        const allowDrop = evaluateDragAllowDrop(event.dataTransfer);
        event.dataTransfer.dropEffect = allowDrop ? "copy" : "none";
        setIsDragOver(allowDrop);
    };

    const handleContainerDragLeave = (event) => {
        // Don't handle container drag leave if we're reordering items
        if (draggedIndex !== null) {
            return;
        }
        
        event.preventDefault();
        event.stopPropagation();
        // Only set isDragOver to false if we're actually leaving the container
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleContainerDrop = async (event) => {
        // Don't handle drop if we're reordering items (check if draggedIndex is set)
        if (draggedIndex !== null) {
            return;
        }
        
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(event.dataTransfer.files);
        if (files.length === 0) {
            return;
        }
        await processFiles(files);
    };

    // Make container clickable to trigger file input
    const handleContainerClick = () => {
        const fileInput = document.getElementById('file-upload');
        if (fileInput) {
            fileInput.click();
        }
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, selectedFiles]);

    // Handle page reload/close
    useEffect(() => {
        const handleBeforeUnload = () => {
            // File objects cannot survive a reload. Persist the rest of the wizard, but never stale photos.
            updateStudioData('images', []);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [updateStudioData]);

    // Handle route change
    useEffect(() => {
        const handleRouteChange = () => {
            if (!location.pathname.includes('studio-erstellen')) {
                // Clear images when leaving studio-erstellen route
                updateStudioData('images', []);
            }
        };

        handleRouteChange(); // Check on mount
        
        return () => {
            handleRouteChange(); // Check on unmount
        };
    }, [location, updateStudioData]);

    return (
        <div className={classes.mainContainer}>
            <div className={classes.textContainer}>
                <h2 className={`${classes.title} titleSemibold`}>Lade Fotos von deinem Studio hoch</h2>
                <span className="slimRegularLarge">Gute Fotos vermitteln Kunden ein genaues Bild davon, was sie erwartet.</span>
                {selectedFiles?.length === 0 && (
                    <>
                        <span className="slimRegularLarge">Tipps für gute Fotos:</span>
                        <ul className="mediumLarge">
                            <li>1. Hohe Bildqualität: <span className="slimRegularLarge">scharfe & klare Fotos</span></li>
                            <li>2. Licht: <span className="slimRegularLarge">Sorge für gute Lichtverhältnisse auf den Fotos</span></li>
                            <li>3. Perspektive: <span className="slimRegularLarge">verschiedene Blickwinkel fotografieren</span></li>
                        </ul>
                    </>
                )}
            </div>

            {selectedFiles?.length > 0 && (
                <div
                    className={classes.addMoreContainer}
                    onClick={handleContainerClick}
                >
                    <div className={classes.addMoreInner}>
                        <span className="slimRegularLarge">Mehr Fotos hinzufügen</span>
                        <span className={classes.addMoreIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                            </svg>
                        </span>
                    </div>
                </div>
            )}
            <div 
                style={{ height: selectedFiles?.length > 0 ? 'fit-content' : '310px' }} 
                className={`${classes.photoPickerContainer} ${selectedFiles?.length > 0 ? classes.photoPickerContainerHasImages : ''} ${isDragOver ? classes.dragging : ''}`}
                onDragOver={handleContainerDragOver}
                onDragLeave={handleContainerDragLeave}
                onDrop={handleContainerDrop}
                onClick={handleContainerClick}
            >
                {selectedFiles?.length === 0 && (
                    <div className={`${classes.photoPickerTextContainer} regularMiddle`}>
                        <span>Mindestens 5 Fotos hinzufügen. Je mehr desto besser.</span>
                        <span>Drag & Drop oder Hochladen</span>
                    </div>
                )}
                
                {selectedFiles?.length > 0 && (
                    <div 
                        className={classes.previewContainer}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {selectedFiles?.map((photo, index) => (
                            <div 
                                key={photo?.id} 
                                data-image-index={index}
                                className={`${classes.previewImageWrapper} ${draggedIndex === index ? classes.dragging : ''} ${dragOverIndex === index ? classes.dragOver : ''}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                onTouchStart={(e) => handleTouchStart(e, index)}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                {index === 0 && (
                                    <span className={`${classes.coverBadge} regularXxs`}>Cover Photo</span>
                                )}
                                <img    
                                    src={photo?.previewUrl}
                                    alt={`Studio preview ${photo?.fileName}`}
                                    className={classes.previewImage}
                                    draggable={false}
                                />
                                <button
                                    onClick={() => handleRemovePhoto(photo)}
                                    className={classes.removeButton}
                                    type="button"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                
                <div className={classes.photoPickerInputContainer}>
                    <input 
                        type="file" 
                        multiple 
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png" 
                        onChange={handleFileChange} 
                        className={`${classes.fileInput} slimRegularLarge`} 
                        style={{ display: 'none' }} 
                        id="file-upload" 
                    />
                    {selectedFiles?.length === 0 && (
                        <label 
                            htmlFor="file-upload" 
                            className={`${classes.uploadButton} mediumLarge`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            Fotos hochladen
                        </label>
                    )}
                </div>
            </div>
            {selectedFiles?.length > 0 && (
                <div
                    className={classes.addMoreContainer}
                    onClick={handleContainerClick}
                >
                    <div className={classes.addMoreInner}>
                        <span className="mediumLarge">Mehr Fotos hinzufügen</span>
                        <span className={classes.addMoreIcon}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" fill="white"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M16.1016 8.63168H0.101562V7.55536H16.1016V8.63168Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M8.63589 0.0999756V16.1H7.55957V0.0999756H8.63589Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                            </svg>
                        </span>
                    </div>
                </div>
            )}
            {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
        </div>
    );
};

export default SixthStep;
